import { useEffect, useState } from 'react';
import pearClient from 'api/pearClient';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { DEFAULT_PAGINATION_LIMIT } from 'api/constants';

const usePaginationState = (params: HookParamsType): HookResultsType => {
  const {
    sortOrder,
    sortProp,
    queryString,
    locationId,
    isLocationSet,
    currentProgram,
    nextCursor,
    prevCursor,
  } = params;
  const limit = params.limit || DEFAULT_PAGINATION_LIMIT;
  const pageLimit = parseInt(limit, 10);

  const [cursorAction, setCursorAction] = useState('');
  const [isPaginating, setIsPaginating] = useState(false);
  const [hasFetchedIds, setHasFetchedIds] = useState(false);

  // hook results
  const [patientIds, setPatientIds] = useState<string[]>([]);
  const [recordCount, setRecordCount] = useState(0);
  const INITIAL_PAGE = 0;
  const initalPage = params.currentPage || INITIAL_PAGE;
  const [currentPage, setCurrentPage] = useState(initalPage);
  const pageCount = Math.ceil(recordCount / pageLimit);

  // pagination controls
  const [baseParams, setBaseParams] = useState({
    sortOrder,
    sortProp,
    queryString,
    locationId,
    limit,
  });
  const [nextParams, setNextParams] = useState({
    ...baseParams,
    nextCursor: '',
  });
  const [prevParams, setPrevParams] = useState({
    ...baseParams,
    prevCursor: '',
  });
  const [currentParams, setCurrentParams] =
    useState<Nullable<HookCurrentPaginateType>>(null);

  //* -----------------------------------------------
  // State management
  //* -----------------------------------------------

  const actions = {
    getNextPage: () => {
      setCurrentPage((curr) => (curr < pageCount ? curr + 1 : curr));
      setCursorAction('next');
      setIsPaginating(true);
      setHasFetchedIds(false);
      setCurrentParams({ currentPage: currentPage + 1, ...nextParams });
    },

    getPrevPage: () => {
      setCurrentPage((curr) => (curr > INITIAL_PAGE ? curr - 1 : curr));
      setCursorAction('prev');
      setIsPaginating(true);
      setHasFetchedIds(false);
      setCurrentParams({ currentPage: currentPage - 1, ...prevParams });
    },

    getFirstPage: () => {
      setCurrentPage(INITIAL_PAGE);
      setBaseParams({ sortOrder, sortProp, queryString, locationId, limit });
      setCursorAction('first');
      setIsPaginating(true);
      setHasFetchedIds(false);
      setCurrentParams(null);
    },
  };

  //* -----------------------------------------------
  // useEffect for BASE params
  //* -----------------------------------------------

  useEffect(() => {
    let isValidToCallSetState = true;
    if (!isLocationSet && currentProgram) {
      setRecordCount(0);
      setPrevParams({
        sortOrder,
        sortProp,
        queryString: '',
        locationId,
        limit,
        prevCursor: '',
      });
      setNextParams({
        sortOrder,
        sortProp,
        queryString: '',
        locationId,
        limit,
        nextCursor: '',
      });
      setPatientIds([]);
      setHasFetchedIds(false);
    }

    if (isLocationSet && currentProgram) {
      const apiParams = {
        limit,
        locationId,
        queryString,
        sortOrder,
        sortProp,
      } as HookCurrentPaginateType;
      if (nextCursor) {
        apiParams.nextCursor = nextCursor;
      } else if (prevCursor) {
        apiParams.prevCursor = prevCursor;
      }

      setHasFetchedIds(false);
      pearClient.clinician
        .getPaginatedPatientSearch(apiParams)
        .then(({ data, meta }) => {
          const patientIds = data.map(({ patientId }: SearchType) => patientId);
          // call order matters
          if (isValidToCallSetState) {
            setCurrentPage(initalPage);
            // set pagination objects
            setNextParams({
              limit,
              locationId,
              queryString,
              sortOrder,
              sortProp,
              nextCursor: meta.nextCursor!,
            });
            setPrevParams({
              limit,
              locationId,
              queryString,
              sortOrder,
              sortProp,
              prevCursor: meta.prevCursor!,
            });
            // set return objects
            setPatientIds(patientIds);
            setRecordCount(meta.count!);
            setHasFetchedIds(patientIds.length !== 0);
          }
        })
        .catch((err) => {
          ErrorInterceptor.error(err);
        });
    }

    /* cleanup / cancel the async call */
    return () => {
      isValidToCallSetState = false;
    };
  }, [
    // - state logic
    isLocationSet,
    currentProgram,
    // - base params
    limit,
    locationId,
    queryString,
    sortOrder,
    nextCursor,
    prevCursor,
    sortProp,
    initalPage,
  ]);

  //* -----------------------------------------------
  // useEffect for PAGINATION actions
  //* -----------------------------------------------

  useEffect(() => {
    let isValidToCallSetState = true;

    const selectCursorParams = (cursorAction: string) => {
      switch (cursorAction) {
        case 'prev':
          return prevParams;
        case 'next':
          return nextParams;
        case 'first':
        default:
          return baseParams;
      }
    };

    const apiParams = selectCursorParams(cursorAction);
    if (isPaginating) {
      setHasFetchedIds(false);
      pearClient.clinician
        .getPaginatedPatientSearch(apiParams)
        .then(({ data, meta }) => {
          const patientIds = data.map(({ patientId }: SearchType) => patientId);

          if (isValidToCallSetState) {
            // reduce calls by disabllin boolean before reseting params
            setIsPaginating(false);
            // set return objects
            setPatientIds(patientIds);
            setRecordCount(meta.count!);
            setHasFetchedIds(patientIds.length !== 0);
            // set pagination objects
            setNextParams({ ...nextParams, nextCursor: meta.nextCursor! });
            setPrevParams({ ...prevParams, prevCursor: meta.prevCursor! });
          }
        })
        .catch((err) => {
          ErrorInterceptor.error(err);
        });
    }

    /* cleanup / cancel the async call */
    return () => {
      isValidToCallSetState = false;
    };
  }, [
    // - pagination actions
    cursorAction,
    isPaginating,
    baseParams,
    nextParams,
    prevParams,
  ]);

  return [
    { currentPage, patientIds, recordCount, hasFetchedIds, currentParams },
    actions,
  ];
};

export default usePaginationState;

//* -----------------------------------------------
// Type casting
//* -----------------------------------------------

export type HookResultsType = [StateType, ActionsType];

export type HookParamsType = {
  currentPage?: number;
  nextCursor?: string;
  prevCursor?: string;
  currentProgram: string | boolean;
  limit?: string;
  isLocationSet: boolean;
  locationId: string;
  queryString: string;
  sortOrder: SortOrderType;
  sortProp: string;
};

export type StateType = {
  currentParams: any;
  recordCount: number;
  currentPage: number;
  patientIds: string[];
  hasFetchedIds: boolean;
};

export type ActionsType = {
  getNextPage: Function;
  getPrevPage: Function;
  getFirstPage: Function;
};

export type HookCurrentPaginateType = {
  currentPage?: number;
  nextCursor?: string;
  prevCursor?: string;
  locationId: string;
  queryString?: string;
  sortOrder: SortOrderType;
  sortProp: string;
};

type SearchType = IFormattedPatientSearchData;
